import axiosInstance from "../axiosConfig";
import { logger } from "utils/logger";
import { 
  DocumentTemplate, GenerateDocumentRequest, UpdateTemplateSchemaRequest,
  Module, ModuleTheme, ModuleTimeTable, Test, Practice,
  PaginatedResponse,
  ModuleSchema,
  DocumentVersion,
  Student
} from "./documentService.types";

// Получение списка шаблонов документов
export const fetchDocumentTemplates = async (): Promise<DocumentTemplate[]> => {
  try {
    const response = await axiosInstance.get<DocumentTemplate[]>(`/documents/templates/`);
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении списка документов:", error);
    throw error;
  }
};

export const fetchDocumentTemplate = async (id: number): Promise<DocumentTemplate> => {
  try {
    const response = await axiosInstance.get<DocumentTemplate>(`/documents/templates/${id}/`);
    return response.data;
  } catch (error) {
    logger.error("Ошибка получениии шаблона документов:", error);
    throw error;
  }
};


export const getDocumentTemplate = async (id: number): Promise<DocumentTemplate> => {
  try {
    const response = await axiosInstance.get<DocumentTemplate>(`/documents/templates/${id}/`);
    return response.data;
  } catch (error) {
    logger.error("Ошибка получениии шаблона документа:", error);
    throw error;
  }
};


// Удаление шаблона документа
export const deleteDocumentTemplate = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/documents/templates/${id}/`);
  } catch (error) {
    logger.error("Ошибка при удалении шаблона документа:", error);
    throw error;
  }
};

// Обновление шаблона документа
export const updateDocumentTemplate = async (id: number, data: Partial<DocumentTemplate>): Promise<DocumentTemplate> => {
  try {
    const response = await axiosInstance.put(`/documents/templates/${id}/`, data);
    return response.data;
  } catch (error) {
    logger.error("Ошибка при обновлении шаблона документа:", error);
    throw error;
  }
};


// --- Сервисы для работы с модулями ---

export const fetchModules = async (
  template_id: string,
  page: number = 1,
  pageSize: number = 10,
  orderBy: string = ""
): Promise<PaginatedResponse<Module>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<Module>>(
      `/documents/versions/${template_id}/modules/`,
      {
        params: { page, page_size: pageSize, ordering: orderBy },
      }
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении модулей:", error);
    throw error;
  }
};


export const createModule = async (
  templateId: number,
  data: Partial<Module & { data?: Record<string, string> }>
): Promise<Module> => {
  try {
    const response = await axiosInstance.post<Module>(
      `/documents/versions/${templateId}/modules/`,
      data
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при создании модуля:", error);
    throw error;
  }
};

export const updateModule = async (id: number, data: Partial<Module>): Promise<Module> => {
  try {
    const response = await axiosInstance.put(`/documents/modules/${id}/`, data);
    return response.data;
  } catch (error) {
    logger.error("Ошибка при обновлении модуля:", error);
    throw error;
  }
};

export const deleteModule = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/documents/modules/${id}/`);
  } catch (error) {
    logger.error("Ошибка при удалении модуля:", error);
    throw error;
  }
};

export const fetchModuleThemes = async (
  module_id: number,
  page: number = 1,
  pageSize: number = 10,
  orderBy: string = ""
): Promise<PaginatedResponse<ModuleTheme>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<ModuleTheme>>(
      `/documents/modules/${module_id}/themes/`,
      {
        params: { page, page_size: pageSize, ordering: orderBy },
      }
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении тем:", error);
    throw error;
  }
};


// --- Сервисы для расписаний ---

export const fetchModuleTimeTables = async (module_id: number, page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<ModuleTimeTable>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<ModuleTimeTable>>(`/documents/modules/${module_id}/timetables/`, {
      params: { page, page_size: pageSize },
    });
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении расписаний:", error);
    throw error;
  }
};

// --- Сервисы для тестов ---

export const fetchTests = async (
  template_id: number,
  page: number = 1,
  pageSize: number = 10,
  orderBy: string = ""
): Promise<PaginatedResponse<Test>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<Test>>(
      `/documents/versions/${template_id}/tests/`,
      {
        params: { page, page_size: pageSize, ordering: orderBy },
      }
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении тестов:", error);
    throw error;
  }
};

export const createTest = async (
  documentId: number,
  data: Partial<Test>
): Promise<Test> => {
  try {
    const response = await axiosInstance.post(`/documents/versions/${documentId}/tests/`, data);
    return response.data;
  } catch (error) {
    logger.error("Ошибка при создании теста:", error);
    throw error;
  }
};

export const updateTest = async (
  id: number,
  data: Partial<Test>
): Promise<Test> => {
  try {
    const response = await axiosInstance.put(`/documents/tests/${id}/`, data);
    return response.data;
  } catch (error) {
    logger.error("Ошибка при обновлении теста:", error);
    throw error;
  }
};

export const deleteTest = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/documents/tests/${id}/`);
  } catch (error) {
    logger.error("Ошибка при удалении теста:", error);
    throw error;
  }
};

// --- Сервисы для практик ---

export const fetchPractices = async (
  template_id: number,
  page: number = 1,
  pageSize: number = 10,
  orderBy: string = ""
): Promise<PaginatedResponse<Practice>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<Practice>>(
      `/documents/versions/${template_id}/practices/`,
      {
        params: { page, page_size: pageSize, ordering: orderBy },
      }
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении практик:", error);
    throw error;
  }
};

export const createPractice = async (
  template_id: number,
  data: Partial<Practice>
): Promise<Practice> => {
  try {
    const response = await axiosInstance.post(`/documents/versions/${template_id}/practices/`, data);
    return response.data;
  } catch (error) {
    logger.error("Ошибка при создании практики:", error);
    throw error;
  }
};

export const updatePractice = async (
  id: number,
  data: Partial<Practice>
): Promise<Practice> => {
  try {
    const response = await axiosInstance.put(`/documents/practices/${id}/`, data);
    return response.data;
  } catch (error) {
    logger.error("Ошибка при обновлении практики:", error);
    throw error;
  }
};

export const deletePractice = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/documents/practices/${id}/`);
  } catch (error) {
    logger.error("Ошибка при удалении практики:", error);
    throw error;
  }
};


// --- Сервисы для расписаний (ModuleTimeTable) ---

export const createModuleTimeTable = async (
  module_id: number,
  data: Partial<ModuleTimeTable>
): Promise<ModuleTimeTable> => {
  try {
    const response = await axiosInstance.post(
      `/documents/modules/${module_id}/timetables/`,
      data
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при создании расписания модуля:", error);
    throw error;
  }
};

export const updateModuleTimeTable = async (
  id: number,
  data: Partial<ModuleTimeTable>
): Promise<ModuleTimeTable> => {
  try {
    const response = await axiosInstance.put(
      `/documents/timetables/${id}/`,
      data
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при обновлении расписания модуля:", error);
    throw error;
  }
};

export const deleteModuleTimeTable = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/documents/timetables/${id}/`);
  } catch (error) {
    logger.error("Ошибка при удалении расписания модуля:", error);
    throw error;
  }
};


// --- Сервисы для тем модуля ---

export const createModuleTheme = async (
  template_id: string,
  data: Record<string, any>
): Promise<ModuleTheme> => {
  try {
    const response = await axiosInstance.post(
      `/documents/modules/${template_id}/themes/`,
      data
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при создании темы модуля:", error);
    throw error;
  }
};

export const updateModuleTheme = async (
  id: number,
  data: Partial<ModuleTheme>
): Promise<ModuleTheme> => {
  try {
    const response = await axiosInstance.put(
      `/documents/themes/${id}/`,
      data
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при обновлении темы модуля:", error);
    throw error;
  }
};

export const deleteModuleTheme = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/documents/themes/${id}/`);
  } catch (error) {
    logger.error("Ошибка при удалении темы модуля:", error);
    throw error;
  }
};


export const fetchModuleSchemas = async (
  templateId: number,
): Promise<ModuleSchema[]> => {
  try {
    const response = await axiosInstance.get<ModuleSchema[]>(
      `/documents/templates/${templateId}/module-schemas/`
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении схем модуля:", error);
    throw error;
  }
};

export const fetchModuleThemeSchemas = async (
  templateId: number,
): Promise<ModuleSchema[]> => {
  try {
    const response = await axiosInstance.get<ModuleSchema[]>(
      `/documents/templates/${templateId}/module-theme-schemas/`
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении схем модуля:", error);
    throw error;
  }
};



// Получение списка версий конкретного шаблона
export const fetchDocumentVersions = async (
  templateId: number,
  page: number = 1,
  pageSize: number = 10,
  orderBy: string = ""
): Promise<PaginatedResponse<DocumentVersion>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<DocumentVersion>>(
      `/documents/templates/${templateId}/versions/`,
      {
        params: { page, page_size: pageSize, ordering: orderBy },
      }
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении списка версий документа:", error);
    throw error;
  }
};


// Создание новой версии документа
export const createDocumentVersion = async (templateId: number, versionName: string): Promise<DocumentVersion> => {
    const response = await axiosInstance.post(`/documents/templates/${templateId}/versions/`, { name: versionName });
    return response.data;
};

// Обновление версии документа
export const updateDocumentVersion = async (versionId: number, versionName: string): Promise<DocumentVersion> => {
    const response = await axiosInstance.put(`/documents/versions/${versionId}/`, { name: versionName });
    return response.data;
};

// Удаление версии документа
export const deleteDocumentVersion = async (versionId: number): Promise<void> => {
    await axiosInstance.delete(`/documents/versions/${versionId}/`);
};

// Генерация документа по версии
export const generateDocument = async (requestData: GenerateDocumentRequest): Promise<Blob> => {
    const response = await axiosInstance.post(`/documents/versions/${requestData.version_id}/generate/`, requestData, {
        responseType: "blob",
    });
    return response.data;
};


export const downloadGeneratedDocument = async (
  versionId: number,
  versionName: string,
  format: "docx" | "pdf" = "docx"
): Promise<void> => {
  try {
    const response = await axiosInstance.post(
      `/documents/versions/${versionId}/generate/?download_format=${format}`,
      {},
      { responseType: "blob" }
    );

    // При необходимости можно проверить статус ответа
    if (response.status !== 200) {
      throw new Error("Ошибка при генерации документа");
    }

    const mimeType =
      format === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

    const extension = format === "pdf" ? "pdf" : "docx";

    const blob = new Blob([response.data], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${versionName}.${extension}`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    throw error;
  }
};


// --- Сервисы для студентов ---

export const fetchStudents = async (
  template_id: number,
  page: number = 1,
  pageSize: number = 10,
  orderBy: string = ""
): Promise<PaginatedResponse<Student>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<Student>>(
      `/documents/versions/${template_id}/students/`,
      {
        params: { page, page_size: pageSize, ordering: orderBy },
      }
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении тестов:", error);
    throw error;
  }
};

export const createStudent = async (
  documentId: number,
  data: Partial<Student>
): Promise<Student> => {
  try {
    const response = await axiosInstance.post(`/documents/versions/${documentId}/students/`, data);
    return response.data;
  } catch (error) {
    logger.error("Ошибка при создании теста:", error);
    throw error;
  }
};

export const updateStudent = async (
  id: number,
  data: Partial<Student>
): Promise<Student> => {
  try {
    const response = await axiosInstance.put(`/documents/students/${id}/`, data);
    return response.data;
  } catch (error) {
    logger.error("Ошибка при обновлении теста:", error);
    throw error;
  }
};

export const deleteStudent = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/documents/students/${id}/`);
  } catch (error) {
    logger.error("Ошибка при удалении теста:", error);
    throw error;
  }
};